import { Controller } from '@hotwired/stimulus'

// Fits wide markdown tables (e.g. invoice remediation Lines/Charges, or an
// extraction verification discrepancy table) to their column, and gives the
// ones that still can't fit a clear "there's more to scroll" affordance.
//
// For each table we, in order:
//   1. pick a padding density from the table's own shape (cols/rows),
//   2. step the density down further if it still overflows its column,
//   3. let a table that still needs room grow past its column to the right,
//      into the space the page has beside it (bleed), never shrinking its type,
//   4. leave genuinely wide tables scrollable, with the first column pinned
//      and a fade on the right edge while there is more to see.
//
// The table is its own overflow box (`display:block; overflow:auto` from
// markdown.css), so `scrollWidth > clientWidth` means it overflows, and the
// edge fade is a scroll-anchored `mask-image` on the table itself — no wrapper,
// and surface-agnostic (it fades the content to transparent rather than to a
// hard-coded background colour, so it works on every card/bubble surface).
//
// Re-evaluated when the column resizes (e.g. the context rail is toggled) so a
// wider viewport can relax a previous decision. Small tables (the common 2–3
// column case) are left at the comfortable default.

const FADE_WIDTH = '2rem' // width of the soft edge fade
const SENTENCE_CELL = 40 // a cell longer than this makes its column a text column
const SHORT_PHRASE = 32 // longest multi-word cell that stays on one line
const EPS = 1 // px slack when testing overflow / scroll extents
const BLEED_GUTTER = 16 // px kept clear of the page edge when a table grows past its column
const WIDE_LABEL = 0.4 // share of the table a first column may take and still be pinned

// A cell that holds one figure: an amount with an optional currency sign, a
// count, or a percentage, possibly negative or in accounting parentheses.
const NUMERIC_CELL = /^\(?[-+\u2212\u2013]?\s?[$\u20ac\u00a3\u00a5]?\s?\d[\d,]*(?:\.\d+)?\s?%?\)?$/
// A cell that says "nothing here" and should not decide a column's kind.
const BLANK_CELL = /^[\s\u2014\u2013-]*$/

// The cell's figure without the small "-41%" delta that a marked price carries
// (ValueCompareHelper), so a column of prices still reads as figures.
function figureText(cell) {
  const delta = cell.querySelector('.vc-delta')
  if (!delta) return (cell.textContent || '').trim()
  const copy = cell.cloneNode(true)
  copy.querySelectorAll('.vc-delta').forEach((node) => node.remove())
  return (copy.textContent || '').trim()
}

// Tags every column whose filled cells are all figures with md-col-num, on the
// head and the body alike, so the stylesheet can set it right in mono figures.
// A cell that holds one word (a part number, a reference, a date stamp) or a
// short phrase ("Item on line 1") gets md-cell-token so it never breaks when
// the column is squeezed: the long cells give way first, and a table that
// still cannot fit scrolls instead of wrapping a word to a line.
// Read at connect and again after a live morph changes the content.
const CELL_MARKS = ['md-cell-token', 'md-col-text', 'md-col-num']
function markCells(table) {
  table.querySelectorAll(CELL_MARKS.map((mark) => `.${mark}`).join(', ')).forEach((cell) => cell.classList.remove(...CELL_MARKS))
  const rows = Array.from(table.querySelectorAll('tbody tr'))
  if (rows.length === 0) return
  rows.forEach((row) => {
    Array.from(row.children).forEach((cell) => {
      const text = (cell.textContent || '').trim()
      if (text && text.length <= (/\s/.test(text) ? SHORT_PHRASE : 24)) cell.classList.add('md-cell-token')
    })
  })
  const headCells = Array.from(table.querySelectorAll('thead th'))
  const width = Math.max(headCells.length, ...rows.map((row) => row.children.length))

  // A column with a sentence in it (md-col-text) keeps a minimum width, so it
  // is never squeezed to a word per line; the table scrolls instead.
  for (let col = 0; col < width; col++) {
    const cells = [headCells[col], ...rows.map((row) => row.children[col])].filter(Boolean)
    if (cells.some((cell) => (cell.textContent || '').trim().length > SENTENCE_CELL)) cells.forEach((cell) => cell.classList.add('md-col-text'))
  }

  for (let col = 0; col < width; col++) {
    let figures = 0
    let other = 0
    rows.forEach((row) => {
      const cell = row.children[col]
      if (!cell) return
      const text = figureText(cell)
      if (BLANK_CELL.test(text)) return
      if (NUMERIC_CELL.test(text)) figures++
      else other++
    })
    if (figures === 0 || other > 0) continue
    if (headCells[col]) headCells[col].classList.add('md-col-num')
    rows.forEach((row) => row.children[col] && row.children[col].classList.add('md-col-num'))
  }
}

// A live broadcast morphs the content in place (method: :morph): it would set
// the classes and styles fitting gave each table and cell back to the
// server's, and it can add, change or remove tables. Those attributes are
// left alone during the morph, and once it is done the tables are found,
// marked and fitted again.
const FITTED_ATTRIBUTES = ['class', 'style']

export default class extends Controller {
  connect() {
    this.scrollHandlers = new Map()
    this.apply = this.apply.bind(this)
    this.keepFit = this.keepFit.bind(this)
    this.afterMorph = this.afterMorph.bind(this)
    this.element.addEventListener('turbo:before-morph-attribute', this.keepFit)
    this.element.addEventListener('turbo:morph-element', this.afterMorph)
    this.refresh()

    // The observed element is the column-width container, not the tables, so
    // re-tiering (which only changes table size) can't feed back into a loop.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.apply)
      this.resizeObserver.observe(this.element)
    }
  }

  disconnect() {
    this.element.removeEventListener('turbo:before-morph-attribute', this.keepFit)
    this.element.removeEventListener('turbo:morph-element', this.afterMorph)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    this.scrollHandlers.forEach((handler, table) => table.removeEventListener('scroll', handler))
    this.scrollHandlers.clear()
    this.refreshQueued = false
  }

  keepFit(event) {
    const target = event.target
    if (FITTED_ATTRIBUTES.includes(event.detail?.attributeName) && target?.closest?.('table') && this.element.contains(target)) event.preventDefault()
  }

  // Turbo reports each morphed element; one pass after the whole morph,
  // before the page is painted.
  afterMorph() {
    if (this.refreshQueued) return
    this.refreshQueued = true
    queueMicrotask(() => {
      if (!this.refreshQueued) return
      this.refreshQueued = false
      this.refresh()
    })
  }

  // Finds the tables as they are now: marks their cells, drops the scroll
  // listeners of tables that are gone, and fits them all.
  refresh() {
    this.tables = Array.from(this.element.querySelectorAll('table'))
    this.scrollHandlers.forEach((handler, table) => {
      if (this.tables.includes(table)) return
      table.removeEventListener('scroll', handler)
      this.scrollHandlers.delete(table)
    })
    this.tables.forEach(markCells)
    this.apply()
  }

  apply() {
    (this.tables || []).forEach((table) => this.fit(table))
  }

  fit(table) {
    // Reset everything we might have set so each pass is deterministic and a
    // wider viewport can relax a previous tier or scale.
    table.classList.remove('md-table--compact', 'md-table--dense')
    table.style.fontSize = ''
    table.classList.remove('md-table--scrolls')

    const headRow = table.querySelector('tr')
    const cols = headRow ? headRow.querySelectorAll('th, td').length : 0
    const rows = table.querySelectorAll('tr').length

    // Shape-based volume heuristic. Kept eager so wide AI tables compact early;
    // the width fallback below still steps narrower tables down only when they
    // actually overflow.
    //
    // Skipped only inside a container that has declared it has room to spare --
    // today, a timeline card that widened itself to fit this very table. Leaving
    // the eager tiers on there would squeeze the table the card just made room
    // for. Every other surface keeps the shape-based tier exactly as before, and
    // the width fallback below still catches anything that overflows.
    if (!table.closest('[data-density-comfortable]')) {
      if (cols >= 4 || rows >= 6) table.classList.add('md-table--compact')
      if (cols >= 6 || rows >= 12) table.classList.add('md-table--dense')
    }

    // Width fallback: step down a tier until the table fits or hits the densest.
    const overflows = () => table.scrollWidth > table.clientWidth + EPS

    if (overflows() && !table.classList.contains('md-table--compact')) {
      table.classList.add('md-table--compact')
    }
    if (overflows() && !table.classList.contains('md-table--dense')) {
      table.classList.add('md-table--dense')
    }

    // No scale-to-fit: a table keeps the text's size. One that needs more room
    // first grows into the space beside its column (bleed), then scrolls.
    this.bleed(table)
    const scrolls = overflows()
    table.classList.toggle('md-table--scrolls', scrolls)
    // A label column wider than 40% of the table is not pinned (markdown.css):
    // on a phone it would leave too little room to scroll the rest past it.
    const label = scrolls ? table.querySelector('tr > :first-child') : null
    table.classList.toggle('md-table--wide-label', !!label && label.getBoundingClientRect().width > table.clientWidth * WIDE_LABEL)

    // Scroll affordance: tables that still overflow fade their overflowing edge
    // and track scroll position; tables that now fit get the fade removed.
    this.ensureScrollListener(table)
    this.updateFade(table)
  }

  // How far the table may grow past its column to the right: up to the edge
  // of the page column that holds it, less a gutter. It starts where the text starts
  // and spends that room before it scrolls, the way a wide table in a
  // document runs out to the margin.
  bleed(table) {
    table.style.removeProperty('--md-bleed')
    const box = table.parentElement
    if (!box) return
    // The page column that may hold it: one marked data-table-bound (the task
    // page's main panel), the timeline's column (the stream reads on a narrower
    // measure inside it), or the first container on the way up that clips. A
    // table with none of these keeps to its column.
    let bound = box
    while (bound && bound !== document.body) {
      if (bound.matches('[data-table-bound]')) break
      if (bound.classList.contains('paperwork-events')) { bound = bound.parentElement; break }
      if (/(hidden|auto|scroll|clip)/.test(getComputedStyle(bound).overflowX)) break
      bound = bound.parentElement
    }
    if (!bound || bound === document.body) return
    const boundStyle = getComputedStyle(bound)
    const limit = bound.getBoundingClientRect().right - parseFloat(boundStyle.paddingRight || 0) - BLEED_GUTTER
    const room = Math.floor(limit - box.getBoundingClientRect().right)
    if (room > EPS) table.style.setProperty('--md-bleed', `${room}px`)
  }

  ensureScrollListener(table) {
    if (this.scrollHandlers.has(table)) return
    const handler = () => this.updateFade(table)
    table.addEventListener('scroll', handler, { passive: true })
    this.scrollHandlers.set(table, handler)
  }

  // Anchors a soft fade to whichever edge has hidden content. Uses a mask so it
  // fades the content itself (surface-agnostic) and stays pinned to the visible
  // edge while the content scrolls underneath.
  updateFade(table) {
    const overflow = table.scrollWidth - table.clientWidth
    if (overflow <= EPS) {
      table.classList.remove('md-table--scrolled')
      table.style.webkitMaskImage = ''
      table.style.maskImage = ''
      return
    }

    table.classList.toggle('md-table--scrolled', table.scrollLeft > EPS)
    const atEnd = table.scrollLeft >= overflow - EPS
    // The left edge never fades: the pinned first column stands there.
    const leftStop = 'black 0'
    const rightStop = atEnd ? 'black 100%' : `black calc(100% - ${FADE_WIDTH}), transparent 100%`
    const mask = `linear-gradient(to right, ${leftStop}, ${rightStop})`
    table.style.webkitMaskImage = mask
    table.style.maskImage = mask
  }
}
